import { CalendarDays, CheckCircle2, ChevronRight } from 'lucide-react';
import { Fragment, type ReactElement } from 'react';
import { Link } from 'react-router';

import { useApiQuery } from '../api.js';
import { useSession } from '../auth.js';
import { EmptyState, ErrorMessage, LinkCard, Loading } from '../design/components.js';
import { MODULE_WEB } from '../modules/registry.js';
import { HomeTile, TodoList } from '../shell/DomainPage.js';
import { GlobalSearch } from '../shell/GlobalSearch.js';
import { useShell } from '../shell/Shell.js';
import { iconOf } from '../shell/icons.js';
import { DECISIONS, homeActions, homeTodo, todoCount } from '../shell/navigation.js';

import '../shell/hub.css';

/**
 * Accueils des deux espaces (révision du 2026-10-31, docs/ux/navigation.md) :
 * - espace bénévole (`/`), comme l'accueil de l'app : ma prochaine activité en grand, puis
 *   mes gros boutons (déclarer mes dispos, mon repas, mes emprunts, importer des photos,
 *   trouver mon prochain engagement…), puis « À faire » seulement s'il y a quelque chose
 *   (sondage en attente, commande prête…) ;
 * - espace gestion (`/gestion`), orienté tâches : boutons d'action nommés (« Importer
 *   l'export e-Protec » en tête), compteurs « à traiter » cliquables, puis les rubriques.
 * Aucun bloc en double ; tout vient de la coque calculée par le serveur selon les droits.
 */

interface MyActivity {
  id: string;
  title: string;
  startsAt: string;
  placeName: string | null;
}

const MES_ACTIVITES = '/engagement/mes-activites';

const dayLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
const timeLabel = (iso: string): string =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

function useNextActivities(): {
  items: MyActivity[];
  pending: boolean;
  success: boolean;
  error: unknown;
} {
  const query = useApiQuery<{ items: MyActivity[] }>('/engagement/me/activities');
  const today = new Date().toISOString().slice(0, 10);
  const items = [...(query.data?.items ?? [])]
    .filter((a) => a.startsAt >= today)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .slice(0, 3);
  return {
    items,
    pending: query.isPending,
    success: query.isSuccess,
    error: query.isError ? query.error : null,
  };
}

function ActivityRows({ items }: { items: readonly MyActivity[] }): ReactElement {
  return (
    <ul className="o-todo">
      {items.map((a) => (
        <li key={a.id}>
          <Link className="o-todo__row" to={`${MES_ACTIVITES}/${a.id}`}>
            <span className="o-todo__value">{dayLabel(a.startsAt)}</span>
            <span className="o-todo__label">
              {a.title}
              {a.placeName !== null && <span className="o-hint"> — {a.placeName}</span>}
            </span>
            <ChevronRight aria-hidden="true" size={16} className="o-todo__chevron" />
          </Link>
        </li>
      ))}
      <li>
        <Link className="o-todo__row" to={MES_ACTIVITES}>
          <span className="o-todo__label">Voir toutes mes activités</span>
          <ChevronRight aria-hidden="true" size={16} className="o-todo__chevron" />
        </Link>
      </li>
    </ul>
  );
}

/** Bénévole : ma prochaine activité, en grand, puis les deux suivantes (comme l'app). */
function NextActivity(): ReactElement {
  const { items, pending, success, error } = useNextActivities();
  const [first, ...rest] = items;
  return (
    <section aria-labelledby="prochaine-activite" className="o-stack o-stack--tight">
      <h2 id="prochaine-activite" className="o-hub-bloc__title">
        Ma prochaine activité
      </h2>
      {pending && <Loading />}
      {error !== null && <ErrorMessage error={error} />}
      {success && first === undefined && (
        <EmptyState title="Aucune activité à venir" icon={CalendarDays}>
          <Link to={MES_ACTIVITES}>Voir mes activités</Link>
        </EmptyState>
      )}
      {first !== undefined && (
        <Link className="o-mission" to={`${MES_ACTIVITES}/${first.id}`}>
          <span className="o-mission__date">{dayLabel(first.startsAt)}</span>
          <span className="o-mission__text">
            <span className="o-mission__title">{first.title}</span>
            <span className="o-hint">
              {timeLabel(first.startsAt)}
              {first.placeName !== null && ` · ${first.placeName}`}
            </span>
          </span>
          <ChevronRight aria-hidden="true" size={20} className="o-todo__chevron" />
        </Link>
      )}
      {first !== undefined && <ActivityRows items={rest} />}
    </section>
  );
}

const HOME_BLOCKS = MODULE_WEB.flatMap((m) => m.homeBlocks ?? []);

/** Accueil de l'espace bénévole (`/`). */
export function Home(): ReactElement {
  const { me } = useSession();
  const { shell } = useShell();
  const first = (me.display_name ?? me.username).split(' ')[0] ?? '';
  const permitted = (path: string): boolean =>
    shell.navigation.some((n) => n.path === path && n.available !== false);
  const planning = permitted(MES_ACTIVITES);
  // Blocs des modules (« Mon engagement ») : seulement si leur écran est permis.
  const blocks = HOME_BLOCKS.filter((b) => permitted(b.path));
  // Pas de doublon : mes activités et les blocs sont déjà sur l'accueil, leurs boutons ne
  // sont pas répétés.
  const shown = new Set([...(planning ? [MES_ACTIVITES] : []), ...blocks.map((b) => b.path)]);
  const actions = homeActions(shell.actions, 'benevole').filter((a) => !shown.has(a.path));
  const todo = homeTodo(shell.tiles, 'benevole');
  return (
    <div className="o-stack o-hub">
      <h1 className="o-home__title">Bonjour {first}</h1>
      {planning && <NextActivity />}
      {blocks.map((b) => (
        <Fragment key={b.key}>{b.render()}</Fragment>
      ))}
      {actions.length > 0 && (
        <nav className="o-home__tiles" aria-label="Que voulez-vous faire ?">
          {actions.map((a, i) => (
            <HomeTile key={a.key} to={a.path} label={a.label} icon={a.icon} primary={i === 0} />
          ))}
        </nav>
      )}
      {todo.length > 0 && (
        <section aria-labelledby="a-faire">
          <h2 id="a-faire" className="o-hub-bloc__title">
            À faire
          </h2>
          <TodoList tiles={todo} label="À faire" />
        </section>
      )}
    </div>
  );
}

/** Accueil de l'espace gestion (`/gestion`), orienté tâches. */
export function GestionHome(): ReactElement {
  const { me } = useSession();
  const { shell, gestion } = useShell();
  const first = (me.display_name ?? me.username).split(' ')[0] ?? '';
  const actions = homeActions(shell.actions, 'gestion');
  const todo = homeTodo(shell.tiles, 'gestion');
  const deciding = todo.some((t) => DECISIONS.has(t.key));
  if (gestion.length === 0)
    return (
      <EmptyState title="Votre profil ne donne accès à aucun écran de gestion.">
        <Link to="/">Retour à l’accueil</Link>
      </EmptyState>
    );
  return (
    <div className="o-stack o-hub">
      <h1 className="o-home__title">Bonjour {first} — que voulez-vous faire ?</h1>
      <div className="o-home__search" role="search">
        <GlobalSearch
          navigation={shell.navigation}
          actions={shell.actions}
          espace="gestion"
          placeholder="Une personne, une activité, un lot, une demande…"
          hotkey={false}
        />
      </div>
      {actions.length > 0 && (
        <nav className="o-home__tiles" aria-label="Actions de gestion">
          {actions.map((a, i) => (
            <HomeTile key={a.key} to={a.path} label={a.label} icon={a.icon} primary={i === 0} />
          ))}
        </nav>
      )}
      <section aria-labelledby="a-traiter-gestion">
        <h2 id="a-traiter-gestion" className="o-hub-bloc__title">
          {deciding ? 'À décider et à traiter' : 'À traiter'}
        </h2>
        {todo.length === 0 ? (
          <EmptyState title="Rien à traiter pour le moment." icon={CheckCircle2} />
        ) : (
          <TodoList tiles={todo} label="À traiter" />
        )}
      </section>
      <section aria-labelledby="rubriques-gestion" className="o-hub-bloc">
        <h2 id="rubriques-gestion" className="o-hub-bloc__title">
          Rubriques
        </h2>
        <div className="o-hub-cards">
          {gestion.map((d) => {
            const n = todoCount(d.key, shell.tiles);
            return (
              <LinkCard
                key={d.key}
                title={n > 0 ? `${d.label} (${String(n)} à traiter)` : d.label}
                description={d.phrase}
                icon={iconOf(d.icon)}
                to={d.path}
              />
            );
          })}
        </div>
      </section>
    </div>
  );
}
